import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePoliciesStore } from './policiesStore';

const PolicyList: React.FC = () => {
  const navigate = useNavigate();
  const { policies, loading, error, fetchPolicies } = usePoliciesStore();

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('-');
    if (!year || !month || !day) return dateString;
    return `${month}/${day}/${year}`;
  };

  const formatCurrency = (value: string): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Policies
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            Manage and view all your policies
          </p>
        </div>
        <button
          onClick={() => navigate('/policies/new')}
          className="px-4 py-2 rounded-lg text-white font-medium"
          style={{ backgroundColor: 'var(--color-primary-500)' }}
        >
          + New Policy
        </button>
      </div>

      <div
        className="rounded-lg border overflow-hidden"
        style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border-light)' }}>
                {['Policy Number', 'Carrier Name', 'Effective Date', 'Premium Amount', 'Quote'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-sm font-semibold"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    Loading policies...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--color-error-500)' }}>
                    {error}
                  </td>
                </tr>
              ) : policies.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    No policies found
                  </td>
                </tr>
              ) : (
                policies.map((policy) => (
                  <tr
                    key={policy.id}
                    style={{ borderBottom: '1px solid var(--color-border-light)' }}
                  >
                    <td className="px-4 py-3 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {policy.policy_number}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                      {policy.carrier_name}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {formatDate(policy.effective_date)}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {formatCurrency(policy.premium_amount)}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {policy.quote_name || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PolicyList;
